import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Target, Hammer, Users, Award, Wrench, ArrowRight, CheckCircle2 } from 'lucide-react';
import { siteImages } from '../data/mock';

const OurMissionPage = () => {
  useEffect(() => { document.title = 'Our Mission | Tool Supply Point'; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        <section className="relative h-64 md:h-80 overflow-hidden bg-dark-900">
          <img src={siteImages.heroLifestyle} alt="Our mission" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/85 to-dark-900/40" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-600/15 border border-athletic-600/30 rounded-sm text-athletic-400 text-xs font-bold uppercase tracking-widest mb-4">
                <Target className="w-3.5 h-3.5" /> Our Purpose
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-bold uppercase mb-3">Our Mission</h1>
              <p className="text-dark-200 max-w-lg">Making dependable tools simple to find and easy to trust.</p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-dark-50 border border-dark-200 rounded-sm p-8 md:p-12 mb-16 text-center">
            <div className="w-20 h-20 bg-athletic-600 rounded-sm flex items-center justify-center mx-auto mb-6">
              <Wrench className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold uppercase mb-6">Dependable tools, made simple</h2>
            <p className="text-dark-600 text-lg leading-relaxed max-w-3xl mx-auto">
              At Tool Supply Point, our mission is straightforward: make it effortless to find gear that actually holds up on the job. We skip the overwhelming aisles and hand-pick a compact lineup of tools we would stake our own projects on &mdash; then back each one with fast shipping and support that answers.
            </p>
          </div>

          <div className="mb-16">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-widest rounded-sm mb-4">What we stand for</span>
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold uppercase">The values on our bench</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Excellence', desc: 'We chase excellence in every tool we curate and every customer we help.' },
                { icon: Hammer, title: 'Grit', desc: 'We love hard-working gear and the honest work it makes possible.' },
                { icon: Users, title: 'Community', desc: 'We build a community of pros and DIYers who trade tips and trust.' },
                { icon: Award, title: 'Quality', desc: 'We never compromise &mdash; every tool clears our bar for build and value.' },
              ].map((value, i) => (
                <div key={i} className="bg-dark-50 border border-dark-200 rounded-sm p-6 hover:border-athletic-400 transition-all group">
                  <div className="w-14 h-14 bg-athletic-50 rounded-sm flex items-center justify-center mb-4 group-hover:bg-athletic-100 transition-colors">
                    <value.icon className="w-7 h-7 text-athletic-700" />
                  </div>
                  <h3 className="font-display font-bold uppercase text-dark-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-dark-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: value.desc }} />
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <div className="aspect-video rounded-sm overflow-hidden border border-dark-200">
                <img src={siteImages.powerBackground} alt="Our promise" className="w-full h-full object-cover" />
              </div>
            </div>
            <div>
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-widest rounded-sm mb-4">Our Promise</span>
              <h2 className="font-display text-3xl text-dark-900 font-bold uppercase mb-6">We&apos;re here for the whole job</h2>
              <p className="text-dark-600 leading-relaxed mb-6">
                Whether you&apos;re kitting out a first toolbox or re-stocking a full workshop, we&apos;re here to help. Our crew is dedicated to matching you with the right tool for the way you work &mdash; no big-box confusion required.
              </p>
              <div className="space-y-4">
                {['Hand-picked hardware and power tools', 'Fast free shipping across the USA', '30-day hassle-free returns', 'Dedicated customer support'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-athletic-600 flex-shrink-0" />
                    <span className="text-dark-900">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center bg-gradient-athletic rounded-sm p-12">
            <h2 className="font-display text-3xl font-bold uppercase text-white mb-4">Join the Tool Supply Point crew</h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">Upgrade your kit with tools chosen for real, hard work.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-dark-900 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-dark-800 transition-all">Shop Now <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OurMissionPage;
